// SEQ-193: pure logic for the "By month" view of the Project performance table.
//
// Tom asked for the Project performance table month on month, like the
// Dashboard's Cost breakdown: a row per project, a column per month, then a
// Total. This module builds that grid from two RPC responses and nothing else:
//
//   - one `groupBy: 'project_month'` response, one row per (project, month),
//     carrying RAW wages (a single-month grain, see wagesMultiplierGrain.ts);
//   - the existing `groupBy: 'project'` rollups the Totals view already shows,
//     carrying MULTIPLIED wages.
//
// THE TOTAL GROUP COMES FROM THE PROJECT GRAIN, NEVER FROM SUMMING THE MONTHS.
// Summing month cells would silently drop the wages multiplier from total cost
// and net profit and disagree with the Totals view (the 4% gap Josh reported on
// 14 September 2026; rollup.ts and wagesMultiplierGrain.ts explain it). So for
// every project the Total group equals the Totals view to the cent, and the
// month cells equal what the project's P&L tab shows for that month. They do
// not add up to the Total for cost and profit, and the table says so
// (showsRawWagesNote in wagesMultiplierGrain.ts).
//
// An EMPTY MONTH IS BLANK, NEVER $0. A project with no row for a month has no
// data for it, so its cell is null. Month and partial-month logic is reused
// from monthOnMonth.ts, not rewritten.
//
// Pure string arithmetic for months, no Date, for the timezone reasons set out
// at the top of monthOnMonth.ts.

import type { PnlAnalyticsRow } from "../../lib/pnl/analyticsClient";
import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";
import { metricsToRollup, type PnlRollup } from "./rollup";
import {
  buildMonthOnMonthTable,
  type MonthOnMonthColumn,
  type MonthOnMonthRangeContext,
} from "./monthOnMonth";
import { exportHeader, exportNumber } from "./tableExport";

/** The most months the RPC returns for the project_month grain. Matches the 24 month cap in the migration. */
export const PROJECT_MONTH_MAX_MONTHS = 24;

/** One metric column, as the table component defines it. Only what this module needs. */
export interface ProjectMonthMetric<K extends string = string> {
  key: K;
  label: string;
  unit: PnlUnit;
}

/** "total" is the Total group, otherwise an ISO 'YYYY-MM-01' month. */
export type ProjectMonthGroupKey = string;
export const TOTAL_GROUP = "total";

export interface ProjectMonthRow {
  projectId: string;
  projectName: string;
  status: string;
  /** One rollup per month the project has data for. A month not in the map is blank. */
  months: Map<string, PnlRollup>;
  /** From the project grain (multiplied wages). Null when the project has no project-grain row. */
  total: PnlRollup | null;
  /** SEQ-232. Months with income but no manual cost row (projectMonthCosts.ts). Marked in the grid. Absent means none. */
  noCosts?: ReadonlySet<string>;
  /** SEQ-232. The subset of `noCosts` left out while "Show months without costs" is off: those cells read blank. */
  excluded?: ReadonlySet<string>;
}

export interface ProjectMonthTable {
  columns: MonthOnMonthColumn[];
  rows: ProjectMonthRow[];
}

export interface ProjectMonthProjectInfo {
  projectId: string;
  projectName: string;
  status: string;
  /** The project-grain rollup the Totals view shows for this project, if any. */
  total: PnlRollup | null;
}

/** Short month for a header: 'Jul 25'. */
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function shortMonthLabel(periodMonth: string): string {
  const year = periodMonth.slice(2, 4);
  const monthIndex = Number(periodMonth.slice(5, 7)) - 1;
  return `${MONTH_NAMES[monthIndex] ?? periodMonth.slice(5, 7)} ${year}`;
}

/**
 * Builds the grid. `projects` is every project the page's filters leave in,
 * in any order (the caller sorts), so a project with no month rows still gets
 * a row of blanks. Month rows for projects not in `projects` are ignored.
 */
export function buildProjectMonthTable(
  months: string[],
  monthRows: PnlAnalyticsRow[],
  projects: ProjectMonthProjectInfo[],
  range?: MonthOnMonthRangeContext,
): ProjectMonthTable {
  // Reuse monthOnMonth's month and partial logic by building a no-series table.
  const { columns } = buildMonthOnMonthTable(months, [], false, range);

  const byProject = new Map<string, Map<string, PnlRollup>>();
  for (const row of monthRows) {
    if (!row.projectId || !row.periodMonth) continue;
    let perMonth = byProject.get(row.projectId);
    if (!perMonth) {
      perMonth = new Map();
      byProject.set(row.projectId, perMonth);
    }
    perMonth.set(row.periodMonth, metricsToRollup(row.metrics));
  }

  const rows: ProjectMonthRow[] = projects.map((p) => ({
    projectId: p.projectId,
    projectName: p.projectName,
    status: p.status,
    months: byProject.get(p.projectId) ?? new Map(),
    total: p.total,
  }));
  return { columns, rows };
}

/** One cell's value: null is blank. */
export function projectMonthCell(
  row: ProjectMonthRow,
  group: ProjectMonthGroupKey,
  metricKey: string,
): number | null {
  if (group !== TOTAL_GROUP && row.excluded?.has(group)) return null; // SEQ-232: left out, blank
  const rollup = group === TOTAL_GROUP ? row.total : (row.months.get(group) ?? null);
  if (!rollup) return null;
  const value = (rollup as unknown as Record<string, number | null>)[metricKey];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Sorts a copy of the rows by one month+metric (or Total+metric) cell. Blanks always sort last, in either direction. Ties fall back to the project name so the order is stable. */
export function sortProjectMonthRows(
  rows: ProjectMonthRow[],
  group: ProjectMonthGroupKey,
  metricKey: string,
  direction: "asc" | "desc",
): ProjectMonthRow[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = projectMonthCell(a, group, metricKey);
    const bv = projectMonthCell(b, group, metricKey);
    if (av === null && bv === null) return a.projectName.localeCompare(b.projectName);
    if (av === null) return 1;
    if (bv === null) return -1;
    if (av === bv) return a.projectName.localeCompare(b.projectName);
    return (av - bv) * sign;
  });
}

/** The export header for one cell: "Jul 25 Revenue ($)", "Jul 25 MTD Revenue ($)" for a partial month, "Total Revenue ($)". */
export function projectMonthExportHeader(
  group: ProjectMonthGroupKey,
  partial: boolean,
  metric: ProjectMonthMetric,
  totalLabel = "Total",
): string {
  const prefix = group === TOTAL_GROUP ? totalLabel : `${shortMonthLabel(group)}${partial ? " MTD" : ""}`;
  return `${prefix} ${exportHeader(metric.label, metric.unit)}`;
}

/** The rows an export writes: project name, status-free, then every month group's visible metrics, then the Total group. Blank cells export as "". */
export function projectMonthExportRecords(
  table: ProjectMonthTable,
  sortedRows: ProjectMonthRow[],
  metrics: ProjectMonthMetric[],
  projectHeader = "Project",
  totalLabel = "Total",
  allProjectsRow?: ProjectMonthRow | null,
): Record<string, string | number>[] {
  // SEQ-232: the All projects row is the first data row, ahead of the sorted projects.
  const exported = allProjectsRow ? [allProjectsRow, ...sortedRows] : sortedRows;
  return exported.map((row) => {
    const record: Record<string, string | number> = { [projectHeader]: row.projectName };
    for (const column of table.columns) {
      for (const metric of metrics) {
        record[projectMonthExportHeader(column.periodMonth, column.partial, metric)] = exportNumber(
          projectMonthCell(row, column.periodMonth, metric.key),
          metric.unit,
        );
      }
    }
    for (const metric of metrics) {
      record[projectMonthExportHeader(TOTAL_GROUP, false, metric, totalLabel)] = exportNumber(
        projectMonthCell(row, TOTAL_GROUP, metric.key),
        metric.unit,
      );
    }
    return record;
  });
}
